import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { axiosInstance } from "../../../api-handle/axiosInstance";
import { FILE_BASE_URL } from "../../../api-handle/apiService";

const ReviewsTable = () => {
    const [reviews, setReviews] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, size: 10 });
    const [filters, setFilters] = useState({ type: "", rating: "", isReplied: "", flag: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [imgError, setImgError] = useState(false);

    const fetchReviews = async () => {
        try {
            setLoading(true);

            // Only include filters that have a value
            const activeFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, value]) => value !== "")
            );

            const params = {
                ...activeFilters,
                page: pagination.page,
                size: pagination.size,
            };

            const res = await axiosInstance.get("/mp/review/", { params });
            setReviews(res.data.reviews || []);
            setPagination(res.data.pagination || { total: 0, page: 1, size: 10 });
            setError("");
        } catch (err) {
            setError("Failed to fetch reviews");
            setReviews([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, [filters, pagination.page]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const getAvatar = (name, src) => {
        const sizeStyle = {
            width: "40px",
            height: "40px",
            objectFit: "cover",
            borderRadius: "50%",
        };

        if (src) {
            return <img src={src} alt={name} style={sizeStyle} />;
        }

        const initials = name
            ?.split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase() || "NA";

        return (
            <div
                style={{
                    ...sizeStyle,
                    backgroundColor: "#0d6efd",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: "600",
                    textTransform: "uppercase",
                }}
            >
                {initials}
            </div>
        );
    };
    const renderStars = (rating) => {
        return (
            <div style={{ display: 'flex', gap: '4px' }}>
                {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: i < rating ? '#facc15' : '#d1d5db' }}>
                        ★
                    </span>
                ))}
            </div>
        );
    };


    const columns = [
        {
            name: "Item",
            selector: (row) => row,
            cell: (row) => {
                const item =
                    row.type === "product"
                        ? row.product
                        : row.type === "gym"
                            ? row.merchant?.gymDetail
                            : row.merchant?.academyDetail;

                // Fallback if item is null or undefined
                if (!item) {
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "50%",
                                    backgroundColor: "#0d6efd",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                }}
                            >
                                NA
                            </div>
                            <span style={{ color: "#000", fontWeight: "500" }}>Unknown Item</span>
                        </div>
                    );
                }

                const image =
                    row.type === "product" ? item?.images?.[0] : item?.coverImage;

                const initials = item.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase() || "NA";

                const isImageValid = image && !imgError;

                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        {isImageValid ? (
                            <img
                                src={image ? `${FILE_BASE_URL}/${image}` : undefined}
                                alt={item.name}
                                onError={() => setImgError(true)}
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: "48px",
                                    height: "48px",
                                    borderRadius: "50%",
                                    backgroundColor: "#0d6efd",
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: "600",
                                    fontSize: "16px",
                                }}
                            >
                                {initials}
                            </div>
                        )}
                        <span style={{ color: "#000", fontWeight: "500" }}>{item.name}</span>
                    </div>
                );
            },
        },
        {
            name: "Reviewer",
            selector: (row) => row.user?.name || "Unknown",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    {getAvatar(row.user?.name || "Unknown", row.user?.birthPhoto)}
                    <div>
                        <div className="text-black font-medium">{row.user?.name || "Unknown"}</div>
                        <div className="text-gray-500 text-sm">{row.user?.email || "N/A"}</div>
                    </div>
                </div>
            ),
        },
        {
            name: "Rating",
            selector: (row) => row.rating,
            cell: (row) => renderStars(row.rating),
            width: "140px",
        },
        {
            name: "Review",
            selector: (row) => row.feedback,
            sortable: false,
            wrap: true,
        },
        {
            name: "Date",
            selector: (row) => row.createdAt,
            cell: (row) => new Date(row.createdAt).toLocaleDateString(),
        },
        {
            name: "WhatsApp",
            cell: (row) => {
                const phone = row?.user?.phone;
                const whatsappLink = phone ? `https://wa.me/${phone.replace(/\D/g, "")}` : null;

                if (!whatsappLink) return null;

                return (
                    <a
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Chat on WhatsApp"
                        style={{ color: "#25D366", display: "flex", alignItems: "center" }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                            fill="currentColor"
                            width="20"
                            height="20"
                        >
                            <path d="M16.003 2.005C8.274 2.005 2 8.271 2 15.995c0 2.688.703 5.273 2.033 7.567L2 30l6.566-1.996a13.985 13.985 0 007.437 2.005h.001c7.73 0 14.003-6.267 14.003-13.993S23.73 2.005 16.003 2.005zm0 25.431a11.41 11.41 0 01-5.884-1.604l-.42-.252-3.902 1.186 1.272-3.804-.274-.436a11.41 11.41 0 01-1.772-6.2c0-6.3 5.134-11.431 11.424-11.431 6.29 0 11.424 5.131 11.424 11.431 0 6.3-5.134 11.41-11.424 11.41zm6.286-8.572c-.345-.173-2.044-1.012-2.36-1.126-.316-.115-.548-.173-.78.174s-.895 1.127-1.098 1.354c-.202.23-.402.26-.747.086-.345-.173-1.457-.536-2.773-1.71-1.026-.917-1.718-2.048-1.92-2.392-.202-.346-.021-.533.152-.706.156-.154.345-.4.518-.6.173-.202.23-.346.345-.576.115-.23.057-.433-.028-.606-.086-.173-.78-1.885-1.069-2.584-.281-.673-.565-.581-.78-.59-.202-.009-.433-.01-.664-.01s-.606.087-.923.433c-.316.346-1.207 1.18-1.207 2.877s1.236 3.334 1.408 3.563c.173.23 2.432 3.717 5.896 5.214.824.356 1.464.57 1.964.728.825.263 1.577.226 2.17.137.662-.099 2.044-.834 2.334-1.638.288-.803.288-1.49.202-1.638-.086-.144-.316-.23-.66-.403z" />
                        </svg>
                    </a>
                );
            },
        },
        // Reviews are read-only for vendors — moderation is admin-only.
    ];

    const handlePageChange = (page) => {
        setPagination((prev) => ({ ...prev, page }));
    };
    const resetFilters = () => {
        setFilters({ type: "", rating: "", isReplied: "", flag: "" });
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    return (
        <div className="p-5">
            <h1 className="text-xl font-bold text-black mb-4">Customer Reviews</h1>

            {/* Filters */}
            <div className="row mb-4">
                <div className="col-12 col-md-3 mb-2">
                    <select
                        name="type"
                        value={filters.type}
                        onChange={handleFilterChange}
                        className="form-select"
                    >
                        <option value="">All Types</option>
                        {/* <option value="product">Product</option> */}
                        <option value="gym">Gym</option>
                        <option value="academy">Academy</option>
                    </select>
                </div>

                <div className="col-12 col-md-3 mb-2">
                    <select
                        name="rating"
                        value={filters.rating}
                        onChange={handleFilterChange}
                        className="form-select"
                    >
                        <option value="">All Ratings</option>
                        {[5, 4, 3, 2, 1].map((r) => (
                            <option key={r} value={r}>
                                {r} Stars
                            </option>
                        ))}
                    </select>
                </div>

                {/* <div className="col-12 col-sm-6 col-md-3">
    <select
      name="isReplied"
      value={filters.isReplied}
      onChange={handleFilterChange}
      className="form-select"
    >
      <option value="">All Replies</option>
      <option value="true">Replied</option>
      <option value="false">Not Replied</option>
    </select>
  </div> */}

                {/* <div className="col-12 col-sm-6 col-md-3">
    <select
      name="flag"
      value={filters.flag}
      onChange={handleFilterChange}
      className="form-select"
    >
      <option value="">All Flags</option>
      <option value="true">Flagged</option>
      <option value="false">Not Flagged</option>
    </select>
  </div> */}

                <div className="col-12 col-md-3 mb-2 d-flex">
                    <button
                        type="button"
                        className="btn btn-secondary w-100"
                        onClick={() => {
                            setFilters({ type: "", rating: "", isReplied: "", flag: "" });
                            setPagination((prev) => ({ ...prev, page: 1 }));
                        }}
                    >
                        Reset Filters
                    </button>
                </div>
            </div>


            {/* Table */}
            <DataTable
                columns={columns}
                data={reviews}
                progressPending={loading}
                noDataComponent={error || "No reviews found"}
                pagination
                paginationServer
                paginationTotalRows={pagination.total}
                paginationDefaultPage={pagination.page}
                onChangePage={handlePageChange}
                persistTableHead
                highlightOnHover
                pointerOnHover
                responsive
            />
        </div>
    );
};

export default ReviewsTable;